import Keycloak from 'keycloak-js'
import { createInterval } from '../utils/timers'

// Keycloak instance (singleton)
let keycloakInstance: Keycloak | null = null
let tokenRefreshCleanup: (() => void) | null = null

// Configuration (matches backend)
const KEYCLOAK_CONFIG = {
  url: import.meta.env.VITE_KEYCLOAK_URL || 'http://localhost:8080',
  realm: 'brainrot-gulch',
  clientId: 'brainrot-gulch-web',
}

// URL parameter names for invite code flow
const INVITE_CODE_PARAM = 'invite_code'
const INVITE_KIND_PARAM = 'invite_kind'

// Session storage keys (must match LandingPage.tsx)
const INVITE_CODE_STORAGE_KEY = 'brainrot_invite_code'
const INVITE_KIND_STORAGE_KEY = 'brainrot_invite_kind'

/**
 * Get stored invite code from session storage
 * Used to pass invite code to Keycloak registration URL
 */
function getStoredInviteFromSession(): { code: string; kind: string } | null {
  const code = sessionStorage.getItem(INVITE_CODE_STORAGE_KEY)
  const kind = sessionStorage.getItem(INVITE_KIND_STORAGE_KEY)
  return code && kind ? { code, kind } : null
}

/**
 * Build callback URL with invite code parameters
 * Keycloak preserves these through the OAuth/email verification flow
 */
export function buildCallbackUrl(inviteCode?: string, inviteKind?: string): string {
  const baseUri = `${window.location.origin}/callback`
  if (!inviteCode) return baseUri

  const params = new URLSearchParams()
  params.set(INVITE_CODE_PARAM, inviteCode)
  if (inviteKind) params.set(INVITE_KIND_PARAM, inviteKind)
  return `${baseUri}?${params.toString()}`
}

/**
 * Extract invite code parameters from current URL
 * Returns null if no invite code present
 */
export function extractInviteFromUrl(): { code: string; kind: string } | null {
  const params = new URLSearchParams(window.location.search)
  const code = params.get(INVITE_CODE_PARAM)
  const kind = params.get(INVITE_KIND_PARAM)
  return code && kind ? { code, kind } : null
}

/**
 * Remove invite and OAuth parameters from URL without triggering navigation
 * Uses replaceState to clean browser history
 */
export function cleanInviteFromUrl(): void {
  const url = new URL(window.location.href)
  url.searchParams.delete(INVITE_CODE_PARAM)
  url.searchParams.delete(INVITE_KIND_PARAM)
  // Also clean OAuth params that Keycloak adds
  url.searchParams.delete('code')
  url.searchParams.delete('state')
  url.searchParams.delete('session_state')
  const cleanUrl = url.searchParams.toString()
    ? `${url.pathname}?${url.searchParams}`
    : url.pathname
  window.history.replaceState({}, document.title, cleanUrl)
}

/**
 * Initialize Keycloak adapter
 * Call once at app startup
 */
export async function initKeycloak(): Promise<Keycloak> {
  if (keycloakInstance) {
    return keycloakInstance
  }

  keycloakInstance = new Keycloak(KEYCLOAK_CONFIG)

  // Override createRegisterUrl to append invite code params to registration URL
  // This allows the Keycloak registration form to display the invite code
  const originalCreateRegisterUrl = keycloakInstance.createRegisterUrl.bind(keycloakInstance)
  keycloakInstance.createRegisterUrl = async (
    options?: Keycloak.KeycloakRegisterOptions,
  ): Promise<string> => {
    const url = await originalCreateRegisterUrl(options)
    const stored = getStoredInviteFromSession()
    if (stored?.code) {
      const urlObj = new URL(url)
      urlObj.searchParams.set(INVITE_CODE_PARAM, stored.code)
      urlObj.searchParams.set(INVITE_KIND_PARAM, stored.kind)
      return urlObj.toString()
    }
    return url
  }

  try {
    // Use 'check-sso' to silently check if user is already logged in
    // This avoids redirect loops on initial load
    const authenticated = await keycloakInstance.init({
      onLoad: 'check-sso',
      pkceMethod: 'S256',
      checkLoginIframe: false, // Disable iframe for better performance
      silentCheckSsoRedirectUri: `${window.location.origin}/silent-check-sso.html`,
    })

    // Set up token refresh
    if (authenticated) {
      setupTokenRefresh(keycloakInstance)
    }

    return keycloakInstance
  } catch (error) {
    console.error('Failed to initialize Keycloak:', error)
    throw error
  }
}

/**
 * Get the Keycloak instance
 * Throws if not initialized
 */
export function getKeycloak(): Keycloak {
  if (!keycloakInstance) {
    throw new Error('Keycloak not initialized. Call initKeycloak() first.')
  }
  return keycloakInstance
}

/**
 * Set up automatic token refresh
 * Refreshes token 30 seconds before expiry
 */
function clearTokenRefresh(): void {
  if (tokenRefreshCleanup) {
    tokenRefreshCleanup()
    tokenRefreshCleanup = null
  }
}

function setupTokenRefresh(keycloak: Keycloak): void {
  // Ensure only one interval is active
  clearTokenRefresh()

  // Update tokens proactively
  tokenRefreshCleanup = createInterval(async () => {
    if (keycloak.authenticated) {
      try {
        // Refresh if token expires in less than 30 seconds
        const refreshed = await keycloak.updateToken(30)
        if (refreshed) {
          console.debug('[Keycloak] Token refreshed')
        }
      } catch (error) {
        console.error('[Keycloak] Token refresh failed:', error)
        // Token refresh failed - user needs to re-authenticate
        clearTokenRefresh()
        keycloak.logout()
      }
    } else {
      clearTokenRefresh()
    }
  }, 10000)
}

/**
 * Login - redirect to Keycloak login page
 */
export function login(redirectUri?: string): void {
  const keycloak = getKeycloak()
  keycloak.login({
    redirectUri: redirectUri || `${window.location.origin}/callback`,
  })
}

/**
 * Register - redirect directly to Keycloak registration page
 * Use this when user has a valid invite code (they're definitely new)
 *
 * @param inviteCode - The validated invite code (passed through Keycloak flow)
 * @param inviteKind - The invite kind (CHILD or ADULT)
 */
export function register(inviteCode?: string, inviteKind?: string): void {
  const keycloak = getKeycloak()
  keycloak.register({
    redirectUri: buildCallbackUrl(inviteCode, inviteKind),
  })
}

/**
 * Logout - redirect to Keycloak logout page
 */
export function logout(): void {
  const keycloak = getKeycloak()
  clearTokenRefresh()
  keycloak.logout({
    redirectUri: window.location.origin,
  })
}

/**
 * Get current access token
 * Returns null if not authenticated
 */
export function getAccessToken(): string | null {
  if (!keycloakInstance?.authenticated) {
    return null
  }
  return keycloakInstance.token || null
}

/**
 * Check if user is authenticated
 */
export function isAuthenticated(): boolean {
  return keycloakInstance?.authenticated || false
}

/**
 * Get user info from token
 */
export function getTokenParsed(): Record<string, unknown> | null {
  if (!keycloakInstance?.authenticated) {
    return null
  }
  return keycloakInstance.tokenParsed || null
}

/**
 * Get Keycloak account management URL
 * For password reset and security settings
 */
export function getAccountUrl(): string {
  const keycloak = getKeycloak()
  return keycloak.createAccountUrl()
}
